import { useEffect, useState } from 'react';
import api from 'config/axiosConfig';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import SolutionCard from '../SolutionCard';
import SolutionProfileTabs from './SolutionProfileTabs';
import { Solution } from 'util/constants';
import { setDocumentTitle } from 'util/pageTitles';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import { AxiosError } from 'axios';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { useUser } from 'util/UserContext';
import { Coauthor } from 'components/Coauthors/CoauthorSelect';

const SolutionProfile = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [solution, setSolution] = useState<Solution | null>(null);
  const [error, setError] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [referenceLink, setReferenceLink] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [coauthors, setCoauthors] = useState<Coauthor[]>(solution?.properties?.coauthors || []);
  const [newCoauthors, setNewCoauthors] = useState<Coauthor[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<Coauthor[]>([]);

  // Validation states
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>('');
  const [descriptionValidationMessage, setDescriptionValidationMessage] = useState<string>('');
  const [referenceLinkValidationMessage, setReferenceLinkValidationMessage] = useState<string>('');

  useEffect(() => {
    setDocumentTitle(`Solution - ${title}`);
  }, [title]);

  useEffect(() => {
    const fetchSolution = async () => {
      if (isNaN(Number(id))) {
        setError('404: Failed to load solution profile. Please try again later.');
      } else {
        try {
          const response = await api.get<Solution>(`/api/solutions/id/${id}`);
          setSolution(response.data);
          setTitle(response.data.name);
          setDescription(response.data.description);
          setReferenceLink(response.data.reference_link);
          setCoauthors(response.data.properties?.coauthors || []);

          if (searchParams.get('edit') === 'true') {
            setSearchParams({});
            if (currentUser?.id === response.data.user_id) {
              setEditMode(true);
            }
          }
        } catch (err) {
          if (err instanceof AxiosError && err.response?.status === 404) {
            setError('404: Failed to load solution profile. Please try again later.');
          } else {
            setError('Failed to load solution profile. Please try again later.');
          }
        }
      }
    };

    fetchSolution();
  }, [id, searchParams, currentUser, navigate, setSearchParams]);

  const validateFields = () => {
    let isValid = true;
    
    if (!title.trim()) {
      setTitleValidationMessage('Title is required');
      isValid = false;
    } else if (title.length > 300) {
      setTitleValidationMessage('Title must be less than 300 characters');
      isValid = false;
    } else {
      setTitleValidationMessage('');
    }

    if (!description.trim()) {
      setDescriptionValidationMessage('Description is required');
      isValid = false;
    } else {
      setDescriptionValidationMessage('');
    }

    if (referenceLink && !referenceLink.trim().match(/^https?:\/\/.+/)) {
      setReferenceLinkValidationMessage('Please enter a valid URL starting with http:// or https://');
      isValid = false;
    } else {
      setReferenceLinkValidationMessage('');
    }

    return isValid;
  };

  const handleSave = async () => {
    if (isSubmitting || !validateFields()) return;

    setIsSubmitting(true);
    try {
      await api.patch(`/api/solutions/id/${id}`, {
        name: title,
        description,
        reference_link: referenceLink,
      });
      
      if (newCoauthors.length > 0 || removedCoauthors.length > 0) {
        try {
          await api.post(`/api/coauthors/solution/${id}/request`, {
            addedCoauthors: newCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            })),
            removedCoauthors: removedCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            }))
          });
        } catch (err) {
          console.error('Error updating coauthors:', err);
        }
      }

      setSolution(prev => prev ? {
        ...prev,
        name: title,
        description,
        reference_link: referenceLink,
        properties: {
          ...prev.properties,
          coauthors: coauthors
        }
      } : null);
      setEditMode(false);
      setNewCoauthors([]);
      setRemovedCoauthors([]);
      setSearchParams({});
    } catch (error) {
      console.error('Error updating solution:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSearchParams({});
  };

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find this solution." 
        errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
        buttonText="View Other Solutions" 
        buttonHref="/solutions/new" 
      /> : 
      <ServerErrorPage />;
  }

  if (!solution) {
    return <LoadingSkeleton count={1} height="h-80" className="mt-4" />;
  }

  const editorButtons = editMode ? (
    <div className="flex justify-end gap-2">
      <button
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting || !description}
      >
        Save
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 relative">
      <div>
        {solution && (
          <SolutionCard
            solution={{...solution, name: title, reference_link: referenceLink}}
            isSolutionProfilePage={true}
            isEntitySection={false}
            truncateDescription={true}
            editMode={editMode}
            onTitleChange={(value: string) => setTitle(value)}
            onDescriptionChange={setDescription}
            onReferenceLinkChange={setReferenceLink}
            titleValidationMessage={titleValidationMessage}
            descriptionValidationMessage={descriptionValidationMessage}
            referenceLinkValidationMessage={referenceLinkValidationMessage}
            onCoauthorsChange={(added: any, removed: any) => {
              // Remove any coauthor in 'removed' from the parent's coauthors.
              setCoauthors(prev => prev.filter(
                (c: any) => !removed.some((r: any) => parseInt(r.id) === parseInt(c.id) && r.type === c.type)
              ));
              // Always update newCoauthors and removedCoauthors with the callback values.
              setNewCoauthors(added);
              setRemovedCoauthors(removed);
            }}
          >
            {editorButtons}
          </SolutionCard>
        )}
      </div>
      <SolutionProfileTabs solution={solution} />
    </div>
  );
};

export default SolutionProfile;
